import { Box } from '@mui/material';
import { t } from 'i18next';
import { useCollectionMeta } from 'json-api-hooks';
import React, { FC, MouseEventHandler, useContext } from 'react';

import Button from '../././Button';

import { CollectionContext } from './Collection';

const OFFSET = 2;
const SECOND_PAGE = 2;
const COLLAPSE_PLACEHOLDER = null;

export interface PaginationProps {
  textAlign?: 'left' | 'right';
}

const PaginationButton = ({
  children,
  icon,
  label,
  page,
}: {
  children?: string;
  icon?: string;
  label?: string;
  page: number;
}) => {
  const { alterCollectionOpts, collectionOpts, resourceType, updateCollection } = useContext(CollectionContext);
  const { pageSize, totalCount } = useCollectionMeta(resourceType, collectionOpts);
  const { page: currentPage } = collectionOpts;
  const pageCount = totalCount && pageSize && Math.ceil(totalCount / pageSize);
  const active = page == (currentPage ?? 1);
  const newOpts = alterCollectionOpts({ page });

  if (!newOpts || !pageCount) {
    return null;
  }

  const onClick: MouseEventHandler = (e) => {
    e.preventDefault();

    updateCollection({ page });
  };
  const disabled = page <= 0 || page > pageCount;

  return (
    <Box component="li" sx={{ display: 'inline-block', listStyleType: 'none' }}>
      <Button small active={active} ariaLabel={label} disabled={disabled} icon={icon} variant="text" onClick={onClick}>
        {children}
      </Button>
    </Box>
  );
};

export const getPaginationButtons = (page: number, pageCount: number) => {
  const SECOND_LAST_PAGE = pageCount - 1;
  const buttons: Array<number | null> = [1];
  const from = Math.max(SECOND_PAGE, page - OFFSET);
  const to = Math.min(SECOND_LAST_PAGE, page + OFFSET);
  if (from > SECOND_PAGE) {
    buttons.push(COLLAPSE_PLACEHOLDER);
  }
  for (let n = from; n <= to; n++) {
    buttons.push(n);
  }
  if (to < SECOND_LAST_PAGE) {
    buttons.push(COLLAPSE_PLACEHOLDER);
  }
  buttons.push(pageCount);

  return buttons;
};

const CollectionPagination: FC<PaginationProps> = ({ textAlign }) => {
  const { collectionOpts, resourceType } = useContext(CollectionContext);
  const { pageSize, totalCount } = useCollectionMeta(resourceType, collectionOpts);
  const { page } = collectionOpts;

  const pageCount = totalCount && pageSize && Math.ceil(totalCount / pageSize);

  if (!pageCount || pageCount <= 1) {
    return null;
  }

  const buttons = getPaginationButtons(page ?? 1, pageCount);

  return (
    <Box
      aria-label={t('collection.pagination.label')!}
      component="nav"
      role="navigation"
      sx={{
        marginBottom: '1em',
        textAlign: textAlign,
      }}
    >
      <ul>
        <PaginationButton icon="arrow-left" label={t('collection.pagination.previousPage')!} page={(page ?? 1) - 1} />
        {buttons.map((button, index) =>
          button ? (
            <PaginationButton
              key={`${index}-${button}`}
              label={t('collection.pagination.goToPage', { page: button })!}
              page={button}
            >
              {button.toString()}
            </PaginationButton>
          ) : (
            <span key={`${index}-${button}`}>...</span>
          ),
        )}
        <PaginationButton icon="arrow-right" label={t('collection.pagination.nextPage')!} page={(page ?? 1) + 1} />
      </ul>
    </Box>
  );
};

export default CollectionPagination;
